
import React, { useState, useEffect, Suspense, lazy } from 'react';
import {
    BookOpen, Headphones, BarChart3, PenTool,
    Mic, Brain, TrendingUp, Zap, LogOut,
    Clock, Layers, Crown, Sparkles, Activity, FastForward, Ticket, Sword, Feather, Menu, Plane
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sun, Moon } from 'lucide-react';
import { ModuleType } from '../types';
const OpticalScanner = lazy(() => import('./modules/OpticalScanner'));
const DataForge = lazy(() => import('./modules/DataForge'));
const SonicAnalyzer = lazy(() => import('./modules/SonicAnalyzer'));
const LogicMatrix = lazy(() => import('./modules/LogicMatrix'));
const EssayArchitect = lazy(() => import('./modules/EssayArchitect'));
const VoiceForge = lazy(() => import('./modules/VoiceForge'));
const MemoryPalace = lazy(() => import('./modules/MemoryPalace'));
const AnalyticsCommand = lazy(() => import('./modules/AnalyticsCommand'));
import CacheStatusWidget from './CacheStatusWidget';
import { PricingPage } from './PricingPage';
import { VocabularyMatrix3D, AudioWaveformTrainer, TimeWarpReading, VocabularySlots, GrammarBattleground, WritingFlowState } from './modules/advanced';
import { authService } from '../services/authService';
import { gamificationService } from '../services/gamificationService';
import { streakService } from '../services/streakService';
import { AchievementPopup } from './AchievementPopup';
import { MetaLabel, PlayfairHeading, NobelDivider, StatusBadge } from './nobel';
import { DepartureBoard, TerminalLoader } from './terminal';

interface CommandCenterProps {
    onLogout: () => void;
    onLogin?: () => void;
    currentUser?: any;
    darkMode: boolean;
    onToggleDarkMode: () => void;
}

const CommandCenter: React.FC<CommandCenterProps> = ({ onLogout, onLogin, currentUser, darkMode, onToggleDarkMode }) => {
    const [activeModule, setActiveModule] = useState<ModuleType>(ModuleType.READING);
    const [sessionTime, setSessionTime] = useState(0);
    const [userTier, setUserTier] = useState<'free' | 'premium' | 'elite'>('free');

    // Gamification State
    const [xp, setXP] = useState(0);
    const [level, setLevel] = useState(1);
    const [streak, setStreak] = useState(0);
    const [rankTitle, setRankTitle] = useState("RECRUIT");
    const [rankColor, setRankColor] = useState("#9ca3af");
    const [levelProgress, setLevelProgress] = useState(0);

    // Notification State
    const [achievementNotification, setAchievementNotification] = useState<any>(null);

    useEffect(() => {
        const timer = setInterval(() => setSessionTime(t => t + 1), 1000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        const updateGamificationState = (currentXp: number, currentLevel: number, currentStreak: number) => {
            setXP(currentXp);
            setLevel(currentLevel);
            setStreak(currentStreak);
            const rank = gamificationService.getRankInfo(currentLevel);
            setRankTitle(rank.title);
            setRankColor(rank.color);
            const progress = gamificationService.getProgressToNextLevel(currentXp, currentLevel);
            setLevelProgress(progress);
        };

        const loadUserData = async (uid: string) => {
            const user = await authService.getUserById(uid);
            if (user) {
                setUserTier(user.tier);
                updateGamificationState(user.stats.xp, user.stats.level, user.stats.streak);
            }
        };

        const userId = sessionStorage.getItem('ielts_user_id');
        if (userId) {
            loadUserData(userId);
            streakService.updateStreak(userId);
        } else if (currentUser && currentUser.isGuest) {
            updateGamificationState(currentUser.stats.xp, currentUser.stats.level, currentUser.stats.streak);
        }

        const handlePricingNav = () => setActiveModule(ModuleType.PRICING);
        const handleXPGained = (e: any) => {
            if (userId) loadUserData(userId);
        };
        const handleAchievement = (e: any) => {
            setAchievementNotification(e.detail);
            if (userId) loadUserData(userId);
        };
        const handleUserStats = (e: any) => {
            const user = e.detail;
            updateGamificationState(user.stats.xp, user.stats.level, user.stats.streak);
            if (user.tier) setUserTier(user.tier);
        };

        window.addEventListener('ielts-navigate-pricing', handlePricingNav);
        window.addEventListener('xp-gained', handleXPGained);
        window.addEventListener('achievement-unlocked', handleAchievement);
        window.addEventListener('user-stats-updated', handleUserStats);

        return () => {
            window.removeEventListener('ielts-navigate-pricing', handlePricingNav);
            window.removeEventListener('xp-gained', handleXPGained);
            window.removeEventListener('achievement-unlocked', handleAchievement);
            window.removeEventListener('user-stats-updated', handleUserStats);
        };
    }, [currentUser]);

    const formatTime = (s: number) => {
        const mins = Math.floor(s / 60);
        const secs = s % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    // Nobel Terminal: Gates instead of Modules
    const gateItems = [
        { id: ModuleType.READING, icon: BookOpen, label: 'Reading', gateNumber: 12 },
        { id: ModuleType.LISTENING, icon: Headphones, label: 'Listening', gateNumber: 14 },
        { id: ModuleType.WRITING_T1, icon: BarChart3, label: 'Writing T1', gateNumber: 16 },
        { id: ModuleType.QUICK_DRILL, icon: Layers, label: 'Logic', gateNumber: 18 },
        { id: ModuleType.WRITING_T2, icon: PenTool, label: 'Writing T2', gateNumber: 20 },
        { id: ModuleType.SPEAKING, icon: Mic, label: 'Speaking', gateNumber: 22 },
        { id: ModuleType.MEMORY_PALACE, icon: Brain, label: 'Memory', gateNumber: 24 },
        { id: ModuleType.VOCAB_MATRIX, icon: Sparkles, label: 'Matrix', gateNumber: 26, premium: true },
        { id: ModuleType.VOCAB_SLOTS, icon: Ticket, label: 'Slots', gateNumber: 28, premium: true },
        { id: ModuleType.AUDIO_WAVEFORM, icon: Activity, label: 'Audio', gateNumber: 30, premium: true },
        { id: ModuleType.TIME_WARP, icon: FastForward, label: 'Warp', gateNumber: 32, premium: true },
        { id: ModuleType.GRAMMAR_BATTLE, icon: Sword, label: 'Battle', gateNumber: 34, premium: true },
        { id: ModuleType.WRITING_FLOW, icon: Feather, label: 'Flow', gateNumber: 36, premium: true },
        { id: ModuleType.ANALYTICS, icon: TrendingUp, label: 'Analytics', gateNumber: 38 },
    ];

    const renderModule = () => {
        return (
            <Suspense fallback={<TerminalLoader />}>
                {(() => {
                    switch (activeModule) {
                        case ModuleType.READING: return <OpticalScanner />;
                        case ModuleType.LISTENING: return <SonicAnalyzer />;
                        case ModuleType.WRITING_T1: return <DataForge />;
                        case ModuleType.QUICK_DRILL: return <LogicMatrix />;
                        case ModuleType.WRITING_T2: return <EssayArchitect />;
                        case ModuleType.SPEAKING: return <VoiceForge />;
                        case ModuleType.MEMORY_PALACE: return <MemoryPalace />;
                        case ModuleType.VOCAB_MATRIX: return <VocabularyMatrix3D />;
                        case ModuleType.VOCAB_SLOTS: return <VocabularySlots />;
                        case ModuleType.AUDIO_WAVEFORM: return <AudioWaveformTrainer />;
                        case ModuleType.TIME_WARP: return <TimeWarpReading />;
                        case ModuleType.GRAMMAR_BATTLE: return <GrammarBattleground />;
                        case ModuleType.WRITING_FLOW: return <WritingFlowState />;
                        case ModuleType.ANALYTICS: return <AnalyticsCommand />;
                        case ModuleType.PRICING: return <PricingPage />;
                        default: return <OpticalScanner />;
                    }
                })()}
            </Suspense>
        );
    };

    const isGuest = currentUser?.isGuest || !currentUser;

    // Get tier display
    const getTierDisplay = () => {
        if (level >= 20) return { text: 'PERMANENT', color: 'var(--color-nobel-gold)' };
        if (level >= 15) return { text: 'BRIGHT', color: 'var(--color-nobel-gold)' };
        if (level >= 10) return { text: 'STUDENT', color: 'var(--color-stone-900)' };
        if (level >= 5) return { text: 'TOURIST', color: 'var(--color-stone-600)' };
        return { text: 'TRANSIT', color: 'var(--color-stone-500)' };
    };

    const tierInfo = getTierDisplay();

    return (
        <div className="h-screen w-screen bg-[#F9F8F4] flex flex-col overflow-hidden">
            <AchievementPopup achievement={achievementNotification} onClose={() => setAchievementNotification(null)} />

            {/* TERMINAL HEADER :: Sharp Integrated Toolbar */}
            <header style={{
                backgroundColor: 'var(--color-stone-900)',
                borderTop: '2px solid var(--color-nobel-gold)',
                borderBottom: '2px solid var(--color-nobel-gold)',
                height: '56px',
                display: 'grid',
                gridTemplateColumns: '320px 1fr auto',
                alignItems: 'center',
                gap: '0',
                padding: '0',
                flexShrink: '0'
            }} className="w-full z-50">
                {/* Col 1: Logo & Title (320px) */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    paddingLeft: '16px',
                    paddingRight: '16px',
                    height: '100%',
                    borderRight: '1px solid var(--color-stone-700)'
                }}>
                    <div style={{
                        width: '32px',
                        height: '32px',
                        backgroundColor: 'var(--color-nobel-gold)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <Plane size={18} style={{ color: 'var(--color-stone-900)' }} />
                    </div>
                    <div>
                        <div style={{ color: 'var(--color-nobel-gold)', fontSize: '0.875rem', fontWeight: '700', lineHeight: '1', fontFamily: 'var(--font-heading)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            TED-TALKS-DZ
                        </div>
                        <div style={{ color: 'var(--color-stone-500)', fontSize: '0.625rem', fontWeight: '700', lineHeight: '1', marginTop: '4px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                            TERMINAL v3.0
                        </div>
                    </div>
                </div>

                {/* Col 2: Status Display (1fr) */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '24px', paddingLeft: '24px' }}>

                    {/* Tier Badge - Sharp */}
                    {!isGuest && (
                        <div style={{
                            backgroundColor: 'rgba(197, 160, 89, 0.1)',
                            border: `1px solid ${tierInfo.color}`,
                            padding: '6px 12px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '2px'
                        }}>
                            <div style={{ color: tierInfo.color, fontSize: '0.625rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px' }}>{tierInfo.text}</div>
                            <div style={{ color: tierInfo.color, fontSize: '0.75rem', fontWeight: '600' }}>LVL {level}</div>
                        </div>
                    )}
                    {/* Streak - Sharp */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        <div style={{ color: 'var(--color-stone-500)', fontSize: '0.625rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px' }}>STREAK</div>
                        <div style={{
                            color: streak >= 7 ? 'var(--color-nobel-gold)' : 'var(--color-stone-400)',
                            fontSize: '1.25rem',
                            fontWeight: '700',
                            fontFamily: 'monospace'
                        }}>
                            {streak.toString().padStart(2, '0')}
                        </div>
                        {streak >= 7 && <span style={{ color: 'var(--color-nobel-gold)' }}>✦</span>}
                    </div>
                </div>

                {/* Col 3: Controls (Auto) - Integrated Toolbar */}
                <div style={{
                    display: 'flex',
                    height: '100%',
                    borderLeft: '1px solid var(--color-stone-700)'
                }}>

                    {/* Mode Toggle - Mechanical Switch */}
                    <button
                        onClick={onToggleDarkMode}
                        style={{
                            backgroundColor: darkMode ? 'var(--color-stone-800)' : 'var(--color-stone-700)',
                            borderRight: '1px solid var(--color-stone-700)',
                            height: '100%',
                            padding: '0 20px',
                            cursor: 'pointer',
                            transition: 'background-color 150ms',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            border: 'none',
                            borderLeft: darkMode ? '2px solid var(--color-nobel-gold)' : 'none'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-stone-600)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = darkMode ? 'var(--color-stone-800)' : 'var(--color-stone-700)'}
                    >
                        {darkMode ? <Sun size={16} style={{ color: 'var(--color-stone-400)' }} /> : <Moon size={16} style={{ color: 'var(--color-stone-400)' }} />}
                        <span style={{ color: 'var(--color-stone-400)', fontSize: '0.75rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px' }}>
                            {darkMode ? 'LIGHT' : 'DARK'}
                        </span>
                    </button>

                    {/* Immigration / Logout */}
                    {isGuest ? (
                        <button
                            onClick={onLogin}
                            style={{
                                backgroundColor: 'var(--color-nobel-gold)',
                                color: 'var(--color-stone-900)',
                                height: '100%',
                                padding: '0 24px',
                                cursor: 'pointer',
                                border: 'none',
                                fontSize: '0.75rem',
                                fontWeight: '700',
                                textTransform: 'uppercase',
                                letterSpacing: '1px',
                                transition: 'opacity 150ms'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                        >
                            IMMIGRATION
                        </button>
                    ) : (
                        <button
                            onClick={onLogout}
                            style={{
                                backgroundColor: 'transparent',
                                height: '100%',
                                padding: '0 20px',
                                cursor: 'pointer',
                                border: 'none',
                                borderLeft: '1px solid var(--color-stone-700)',
                                color: 'var(--color-stone-500)',
                                transition: 'color 150ms'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-nobel-gold)'}
                            onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-stone-500)'}
                        >
                            <LogOut size={16} />
                        </button>
                    )}
                </div>
            </header>

            {/* V13 HOLY TRINITY GRID */}
            <div className="terminal-layout" style={{
                display: 'grid',
                gridTemplateColumns: '320px 1fr 450px',
                height: 'calc(100vh - 56px)',
                overflow: 'hidden'
            }}>
                {/* DESKTOP SIDEBAR :: Departure Gates */}
                {/* COL 1: NAVIGATION (320px Fixed) */}
                <aside className="terminal-nav" style={{
                    backgroundColor: 'var(--bg-elevated)',
                    borderRight: '1px solid var(--border-subtle)',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '24px',
                    position: 'relative',
                    zIndex: 100,
                    overflowY: 'auto'
                }}>
                    <div className="mb-6">
                        <MetaLabel>DEPARTURE GATES</MetaLabel>
                        <NobelDivider />
                    </div>

                    <nav className="flex-1 space-y-2 overflow-auto">
                        {gateItems.map((gate) => (
                            <button
                                key={gate.id}
                                onClick={() => setActiveModule(gate.id)}
                                className="gate-card w-full text-left p-4 transition-all group"
                                style={{
                                    backgroundColor: activeModule === gate.id ? 'var(--color-cream)' : 'transparent',
                                    borderColor: activeModule === gate.id ? 'var(--color-nobel-gold)' : 'var(--color-stone-200)',
                                    position: 'relative',
                                    zIndex: 50,
                                    cursor: 'pointer',
                                    pointerEvents: 'auto'
                                }}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <MetaLabel>GATE {gate.gateNumber}</MetaLabel>
                                    {activeModule === gate.id && <StatusBadge status="boarding" />}
                                </div>

                                <div className="flex items-center gap-3">
                                    <gate.icon size={20} style={{ color: 'var(--color-stone-600)' }} />
                                    <span className="body-inter font-medium">{gate.label}</span>
                                </div>

                                {gate.premium && (
                                    <div className="mt-2">
                                        <MetaLabel style={{ fontSize: '0.625rem', color: 'var(--color-nobel-gold)' }}>
                                            FAST-TRACK
                                        </MetaLabel>
                                    </div>
                                )}
                            </button>
                        ))}
                    </nav>

                    <div className="mt-auto pt-4" style={{ borderTop: '1px solid var(--color-stone-200)' }}>
                        <CacheStatusWidget />
                    </div>
                </aside>

                {/* COL 2: MAIN CONTENT (1fr Fluid) */}
                <main className="terminal-content" style={{
                    backgroundColor: 'var(--bg-primary)',
                    padding: '32px',
                    position: 'relative',
                    overflowY: 'auto'
                }}>
                    <div className="h-full overflow-auto">
                        {renderModule()}
                    </div>
                </main>

                {/* COL 3: TOOLS PANEL (450px Fixed) */}
                <aside className="terminal-tools" style={{
                    backgroundColor: 'var(--bg-elevated)',
                    borderLeft: '1px solid var(--border-subtle)',
                    padding: '24px',
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px'
                }}>
                    <div>
                        <MetaLabel>EXTRACTION NODE</MetaLabel>
                        <NobelDivider />
                    </div>

                    {/* Module-specific tools will render here */}
                    <div style={{
                        color: 'var(--color-stone-600)',
                        fontSize: '0.875rem',
                        textAlign: 'center',
                        padding: '48px 0'
                    }}>
                        Select text or interact with the module to see analysis tools.
                    </div>
                </aside>
            </div>

            {/* MOBILE BOTTOM NAV */}
            <nav style={{
                backgroundColor: 'var(--color-stone-white)',
                borderTop: '1px solid var(--color-stone-200)'
            }} className="md:hidden fixed bottom-0 left-0 right-0 h-16 flex items-center justify-around px-2 z-50">
                {gateItems.slice(0, 5).map((gate) => (
                    <button
                        key={gate.id}
                        onClick={() => setActiveModule(gate.id)}
                        className="flex flex-col items-center gap-1 p-2 transition-all"
                        style={{
                            color: activeModule === gate.id ? 'var(--color-nobel-gold)' : 'var(--color-stone-500)'
                        }}
                    >
                        <gate.icon size={20} />
                        <MetaLabel style={{ fontSize: '0.5rem' }}>{gate.label}</MetaLabel>
                    </button>
                ))}
                <button
                    onClick={() => setActiveModule(ModuleType.ANALYTICS)}
                    className="flex flex-col items-center gap-1 p-2"
                    style={{
                        color: activeModule === ModuleType.ANALYTICS ? 'var(--color-nobel-gold)' : 'var(--color-stone-500)'
                    }}
                >
                    <Menu size={20} />
                    <MetaLabel style={{ fontSize: '0.5rem' }}>MORE</MetaLabel>
                </button>
            </nav>

            {/* NOBEL FOOTER :: Signature Block */}
            <footer style={{
                backgroundColor: 'var(--color-stone-white)',
                borderTop: '1px solid var(--color-stone-200)'
            }} className="hidden md:flex h-12 px-8 items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                        <div style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            backgroundColor: 'var(--color-nobel-gold)'
                        }} />
                        <MetaLabel style={{ fontSize: '0.625rem' }}>TERMINAL ONLINE</MetaLabel>
                    </div>
                    <MetaLabel style={{ fontSize: '0.625rem' }}>SESSION: {formatTime(sessionTime)}</MetaLabel>
                </div>

                <div className="signature-block" style={{ border: 'none', padding: '0', margin: '0' }}>
                    <span className="signature-name" style={{ fontSize: '0.875rem' }}>Ted Khercha</span>
                    <span className="signature-handle" style={{ marginLeft: '0.5rem' }}>TED.TALKS.DZ</span>
                </div>
            </footer>
        </div>
    );
};

export default CommandCenter;
